import { Invoice } from '@wonderschool/common-base-types';
import { Button, Dialog, InformationalBanner, Input, Label } from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRefundForInvoice } from '../../../../api/firebase/invoices';
import { InvoiceStatus } from '../../../../helpers/invoices';
import { USER_STATUS } from '../../../../helpers/userStatus';
import { currencyFormatter } from '../../../../helpers/utils';
import { Alert } from '../../../Shared/AppAlert';

const STRIPE_REFUND_MAX_DAYS = 180;
const STRIPE_CHARGE_NOT_REFUNDABLE_ERROR = 'charge_not_refundable';

export function invoiceCanRefund(
  invoice,
  { isSelfServeRefundsEnabled }: { isSelfServeRefundsEnabled: boolean }
): boolean {
  const inv = invoice as any;
  const refundablePayments = inv?.payments?.filter((payment) => payment?.status === 'paid');

  return (
    isSelfServeRefundsEnabled &&
    invoice?.status === InvoiceStatus.PAID &&
    invoice?.userStatus != USER_STATUS.PAID_MANUALLY &&
    refundablePayments?.length > 0
  );
}

type InvoiceRefundDialogProps = {
  invoice: Invoice;
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
  onRefunded?: (updatedData: Partial<Invoice>) => void;
};

export function InvoiceRefundDialog({
  invoice,
  organizationId,
  isOpen,
  onClose,
  onRefunded,
}: InvoiceRefundDialogProps) {
  const { t } = useTranslation();
  const { isAbove5KRefundAllowed } = useFlags();
  const [isRefunding, setIsRefunding] = useState(false);
  const [refundAlert, setRefundAlert] = useState<Alert | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showRefundErrorDialog, setShowRefundErrorDialog] = useState(false);
  const [invoicePaymentRefundReason, setInvoicePaymentRefundReason] = useState('');

  const isUserAllowedToRefund = (): boolean => {
    // For now, we want to limit refunds to invoices that are less than $5,000. We're adding a LaunchDarkly
    // flag that we can toggle for a specific user if we determine it's a legitimate refund.
    const LARGE_INVOICE_GUARDRAIL = 5000;
    return (invoice as any).paidAmount < LARGE_INVOICE_GUARDRAIL || isAbove5KRefundAllowed;
  };

  const isInvoiceInRefundWindow = (): boolean => {
    const inv = invoice as any;
    const refundablePayments = inv.payments?.filter((payment) => payment?.status === 'paid');
    const isInvoiceRefundable = refundablePayments?.length === 1;

    return (
      isInvoiceRefundable &&
      moment().subtract(STRIPE_REFUND_MAX_DAYS, 'days').isBefore(moment(refundablePayments[0].createdAt))
    );
  };

  // When the dialog opens, decide whether to show the confirm-refund dialog or
  // the refund-error dialog (mirrors the old handleRefundClick guard).
  useEffect(() => {
    if (!isOpen) {
      setShowRefundModal(false);
      setShowRefundErrorDialog(false);
      setRefundAlert(null);
      return;
    }
    setInvoicePaymentRefundReason('');
    setRefundAlert(null);
    if (isUserAllowedToRefund() && isInvoiceInRefundWindow()) {
      setShowRefundModal(true);
    } else {
      setShowRefundErrorDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleRefundFail = (error: unknown, translation: string) => {
    if (error) console.error(error);

    setRefundAlert({
      type: 'error',
      message: translation,
    });
    setShowRefundModal(false);
    setIsRefunding(false);
    // Do NOT call onClose() here: in the list host the dialog is conditionally
    // mounted, so closing would unmount this component and destroy the failure
    // message before it paints. Leave the component mounted and surface the
    // error in a dialog; the user dismisses it (which calls onClose).
  };

  const onRefundSuccess = () => {
    onRefunded?.({
      status: InvoiceStatus.REFUND_STARTED,
      userStatus: InvoiceStatus.REFUND_STARTED,
    } as Partial<Invoice>);
    onClose();
  };

  const createRefundWithOptimisticUpdate = async () => {
    setIsRefunding(true);

    try {
      const response = await createRefundForInvoice({ invoiceId: invoice.id, organizationId });

      if (response.error) {
        // Stripe API call failed. Send try again message
        if (response.error.errorType === STRIPE_CHARGE_NOT_REFUNDABLE_ERROR) {
          return handleRefundFail(response.error, 'billing.invoice.startRefundPaymentNotRefundableError');
        } else {
          return handleRefundFail(response.error, 'billing.invoiceDetails.genericRefundError');
        }
      }
    } catch (error) {
      // Problem with the request. Send contact support message
      return handleRefundFail(error, 'userStatusDescription.refundThrowError');
    }
    // Close only after the call resolves so the Confirm button's loading state
    // is visible for the whole round-trip (and the list scroll restores on time).
    setShowRefundModal(false);
    setIsRefunding(false);
    onRefundSuccess();
  };

  const handleCreateRefund = async () => {
    createRefundWithOptimisticUpdate();
  };

  return (
    <>
      <Dialog isOpen={showRefundModal} onClose={onClose}>
        <Dialog.Panel>
          <Dialog.Title>{t('billing.invoiceDetails.startRefund')}</Dialog.Title>
          <Label extraClasses="pt-2">{t('billing.invoiceDetails.amountRefunding')}</Label>
          <Label extraClasses="pt-1 font-semibold">{currencyFormatter((invoice as any).total, { precision: 2 })}</Label>
          <div className="pt-4">
            <Input
              inputType="text"
              label={t('billing.invoiceDetails.refundReason')}
              placeholder={t('billing.invoiceDetails.enterRefundReason')}
              value={invoicePaymentRefundReason}
              onChange={(e) => setInvoicePaymentRefundReason(e.target.value)}
            />
            <Label extraClasses="pt-1 font-light">{t('billing.invoiceDetails.refundReasonInfo')}</Label>
          </div>
          <div className="pt-4">
            <InformationalBanner
              label={t('billing.invoiceDetails.refundInfo', {
                payerName: (invoice as any)?.billedTo?.displayName,
                refundedAmount: currencyFormatter((invoice as any).total, { precision: 2 }),
              })}
              type="info"
            />
          </div>
        </Dialog.Panel>
        <Dialog.Actions>
          <div className="flex justify-center space-x-4">
            <Button label={t('common.cancel')} onClick={onClose} />
            <Button
              primary
              type="submit"
              loading={isRefunding}
              disabled={isRefunding}
              onClick={handleCreateRefund}
              label={t('billing.invoiceDetails.confirmRefundAction')}
            />
          </div>
        </Dialog.Actions>
      </Dialog>
      <Dialog isOpen={showRefundErrorDialog} onClose={onClose}>
        <Dialog.Panel>
          <Dialog.Title>{t('billing.invoice.refundErrorModalTitle')}</Dialog.Title>
          <div>
            {t(
              isUserAllowedToRefund()
                ? 'billing.invoice.startRefundAfter180DaysError'
                : 'billing.invoice.startRefund5KError'
            )}
          </div>
        </Dialog.Panel>
        <Dialog.Actions>
          <Button label={t('common.cancel')} onClick={onClose} />
        </Dialog.Actions>
      </Dialog>
      <Dialog isOpen={!!refundAlert} onClose={onClose}>
        <Dialog.Panel>
          <Dialog.Title>{t('billing.invoice.refundErrorModalTitle')}</Dialog.Title>
          <div>{t(refundAlert?.message ?? '')}</div>
        </Dialog.Panel>
        <Dialog.Actions>
          <Button label={t('common.cancel')} onClick={onClose} />
        </Dialog.Actions>
      </Dialog>
    </>
  );
}
